import type { ErpModule } from '@/api/platform.api';
import { enabledHeadModuleCodes } from '@/config/moduleEntries';
import { decodeJwt } from '@/utils/jwt';
import { STORAGE_KEYS } from '@/constants/storage';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import {
  getModuleEntryByErpModule,
  isPathAllowedForEntry,
  type ModuleEntry,
} from '@/config/moduleEntries';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import { REQUIRED_PASSWORD_CHANGE_PATH } from '@/constants/authRoutes';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { isOrgOwner } from '@/utils/moduleAccess';
import { isPublicAnonymousRoute } from '@/constants/publicAnonymousRoutes';

export const HR_ESS_HOME_PATH = '/hr/ess';

/** Self-service portal access — not an HR admin / operational permission. */
const HR_ESS_SELF_SERVICE_CODES = new Set(['HR_ESS_ACCESS']);

function isHrOperationalPermission(code: string): boolean {
  if (HR_ESS_SELF_SERVICE_CODES.has(code)) return false;
  return code === 'HR_VIEW' || code.startsWith('HR_');
}

export function readEntryModuleFromToken(accessToken: string | null): ErpModule | null {
  if (!accessToken) return null;
  const claim = decodeJwt(accessToken)?.entryModule;
  return claim ? (claim as ErpModule) : null;
}

export function readStoredEntryModule(): ErpModule | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.ENTRY_MODULE);
  return raw ? (raw as ErpModule) : null;
}

export function persistEntryModule(entryModule: ErpModule | null | undefined) {
  if (entryModule) {
    sessionStorage.setItem(STORAGE_KEYS.ENTRY_MODULE, entryModule);
    persistLastModuleSignIn(entryModule);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.ENTRY_MODULE);
  }
}

export function readStoredWorkspaceModule(): ErpModule | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.WORKSPACE_MODULE);
  return raw ? (raw as ErpModule) : null;
}

export function persistWorkspaceModule(module: ErpModule | null | undefined) {
  if (module) {
    sessionStorage.setItem(STORAGE_KEYS.WORKSPACE_MODULE, module);
    persistLastModuleSignIn(module);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.WORKSPACE_MODULE);
  }
}

/**
 * Survives logout storage clears so session-expiry redirects still return to the
 * same module login when the path is a shared/global route.
 */
export function persistLastModuleSignIn(module: ErpModule | null | undefined) {
  if (module) {
    sessionStorage.setItem(STORAGE_KEYS.LAST_MODULE_SIGN_IN, module);
  }
}

export function readLastModuleSignIn(): ErpModule | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.LAST_MODULE_SIGN_IN);
  return raw ? (raw as ErpModule) : null;
}

export function clearLastModuleSignIn() {
  sessionStorage.removeItem(STORAGE_KEYS.LAST_MODULE_SIGN_IN);
}

/** First subscribed head module for default workspace after login. */
export function resolveDefaultWorkspaceModule(enabledModules: string[]): ErpModule | null {
  const codes = enabledHeadModuleCodes(enabledModules);
  return codes.length > 0 ? (codes[0] as ErpModule) : null;
}

export function clearEntryModuleStorage() {
  sessionStorage.removeItem(STORAGE_KEYS.ENTRY_MODULE);
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  sessionStorage.removeItem(STORAGE_KEYS.WORKSPACE_MODULE);
  // Intentionally keep LAST_MODULE_SIGN_IN for post-logout / 401 redirects.
}

const USED_WORKSPACE_MODULES_KEY = 'fattahone_used_workspace_modules';

/** Remember which head-module apps this browser session has opened (for app launcher visibility). */
export function markWorkspaceModuleUsed(module: ErpModule | null | undefined) {
  if (!module) return;
  try {
    const raw = localStorage.getItem(USED_WORKSPACE_MODULES_KEY);
    const used = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    used.add(module);
    localStorage.setItem(USED_WORKSPACE_MODULES_KEY, JSON.stringify([...used]));
  } catch {
    localStorage.setItem(USED_WORKSPACE_MODULES_KEY, JSON.stringify([module]));
  }
}

export function hasUsedMultipleWorkspaceModules(): boolean {
  try {
    const raw = localStorage.getItem(USED_WORKSPACE_MODULES_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as string[]).length >= 2;
  } catch {
    return false;
  }
}

export function clearUsedWorkspaceModules() {
  localStorage.removeItem(USED_WORKSPACE_MODULES_KEY);
}

export function getActiveModuleEntry(entryModule: ErpModule | null | undefined): ModuleEntry | undefined {
  if (!entryModule) return undefined;
  return getModuleEntryByErpModule(entryModule);
}

export function isRouteAllowedForEntryModule(pathname: string, entryModule: ErpModule | null | undefined): boolean {
  if (!entryModule) return true;
  if (pathname === REQUIRED_PASSWORD_CHANGE_PATH) return true;
  if (isPublicAnonymousRoute(pathname)) return true;
  if (isGlobalWorkspaceRoute(pathname)) return true;
  if (isPlatformAdminRoute(pathname)) return true;
  const entry = getModuleEntryByErpModule(entryModule);
  if (!entry) return true;
  if (pathname === '/onboarding') return true;
  return isPathAllowedForEntry(pathname, entry);
}

/** Staff who may open HR for self-service but lack HR admin permissions (e.g. custom role with no HR_* codes). */
export function isHrEssOnlyUser(
  permissions: readonly string[],
  accessibleModules?: string[],
  roleCode?: string | null,
): boolean {
  if (!accessibleModules?.includes('HR')) return false;
  if (isOrgOwner(permissions, roleCode)) return false;
  return !permissions.some(isHrOperationalPermission);
}

export function resolveModuleHomePath(
  entryModule: ErpModule | null | undefined,
  permissions: readonly string[] = [],
  accessibleModules?: string[],
  roleCode?: string | null,
  fallback = MODULE_HUB_PATH,
): string {
  if (!entryModule) return fallback;
  if (entryModule === 'HR' && isHrEssOnlyUser(permissions, accessibleModules, roleCode)) {
    return HR_ESS_HOME_PATH;
  }
  return getModuleEntryByErpModule(entryModule)?.homePath ?? fallback;
}

export type PostLoginPathContext = {
  permissions?: readonly string[];
  accessibleModules?: string[];
  roleCode?: string | null;
};

export function getPostLoginPath(
  entryModule: ErpModule | null | undefined,
  fallback: string,
  context?: PostLoginPathContext,
): string {
  if (context) {
    return resolveModuleHomePath(
      entryModule,
      context.permissions ?? [],
      context.accessibleModules,
      context.roleCode,
      fallback,
    );
  }
  if (!entryModule) return fallback;
  return getModuleEntryByErpModule(entryModule)?.homePath ?? fallback;
}
